import React from "react";
import { Button } from "@/components/ui/button";
import { PermissionButton } from "@/components/ui/permission-button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Shield, FileText, AlertTriangle, RefreshCw, AlertCircle, Filter, MoreHorizontal, Trash2, Users } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plan, Enrollment } from "@shared/schema";
import { planTypeRank } from "@shared/schemas/planTypes";
import { getPlanEnrollmentStatus } from "@/lib/planVisibility";
import { useFsaConfigs } from "@/hooks/useFsaConfigs";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { parseDateLocal } from "@/lib/utils";
import { planEnrollmentCounts, formatEnrolledLabel } from "@/lib/planEnrollmentCounts";
import {
  defaultPlanStateFilter,
  planListEmptyState,
  type PlanStateFilter,
} from "@/lib/planStateFilter";
import { hasLaterPlanYear, isRenewalWindowOpen } from "@shared/utils/planYearLinks";
import { usePermissions } from "@/hooks/usePermissions";
import { RenewPlanDialog } from "@/components/ui/plans/RenewPlanDialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface AvailablePlansProps {
  plansData: Plan[];
  /** True while the plan list is still being fetched: the default filter is
   * decided once this turns false, and the empty row never says "Add First
   * Plan" before then. */
  plansLoading?: boolean;
  getEffectivePlan: (plan: Plan) => Plan;
  formatDateSafe: (date: string | Date | null | undefined) => string;
  deletePlanMutation?: any;
  deleteMode?: "removeFromClient" | "removeFromAgency";
  setLocation?: (location: string) => void;
  clientId?: string;
  prospectId?: string;
  agencyId?: string; // For agency-level plans (not client-specific)
  onAddPlanClick?: () => void;
  // Optional employer-friendly overrides
  primaryActionLabel?: string;
  onPrimaryAction?: () => void;
  showDelete?: boolean;
  showViewDetails?: boolean;
}

const AvailablePlans = ({
  plansData,
  plansLoading = false,
  getEffectivePlan,
  formatDateSafe,
  deletePlanMutation,
  deleteMode = "removeFromClient",
  setLocation,
  clientId,
  prospectId,
  agencyId,
  onAddPlanClick,
  primaryActionLabel,
  onPrimaryAction,
  showDelete = true,
  showViewDetails = true,
}: AvailablePlansProps) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const entityId = clientId || prospectId;
  const clientIdNum = Number(entityId);

  // Agency Benefits tab: the agency's own plans (agency_plans links), no
  // client or prospect behind the list.
  const isAgencyContext = !!agencyId && !entityId;
  const editPermission = isAgencyContext ? "agency:benefits" : "clients:edit";

  const { can } = usePermissions();
  const canEditPlans = can(editPermission);

  const [lastRenewal, setLastRenewal] = React.useState<
    { fromPlanId: number; toPlanId: number } | null
  >(null);
  // The plan year open in the renew wizard, and the action awaiting a yes/no.
  const [renewDialogPlan, setRenewDialogPlan] = React.useState<any | null>(null);
  const [confirmAction, setConfirmAction] = React.useState<
    { kind: "copyRenew" | "remove"; plan: any } | null
  >(null);

  const [reviewPlan, setReviewPlan] = React.useState<
    { id: number; name: string } | null
  >(null);
  const [isReviewDialogOpen, setIsReviewDialogOpen] = React.useState(false);
  // Arriving with ?newPlans= means we just created plans and are pointing at
  // them. A plan whose year starts next month is "future", which the
  // "Available" filter hides — so the plans would be invisible and look like
  // the create failed. Show everything in that case.
  const arrivedWithNewPlans = new URLSearchParams(window.location.search).has("newPlans");
  // The filter the user picked (or a flow set, e.g. after a renewal). Null
  // until then: the list opens on a default decided from the plan counts
  // once they have loaded (DEV-319: "Available (0)" hid two Future plans).
  const [chosenPlanStateFilter, setPlanStateFilter] = React.useState<PlanStateFilter | null>(null);

  // Helper function to compute plan state based on dates
  const getPlanState = (plan: Plan): "closed" | "available" | "future" => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    
    const effectiveDate = plan.effectiveDate ? parseDateLocal(plan.effectiveDate) : null;
    const endDate = (plan as any).endDate ? parseDateLocal((plan as any).endDate) : null;
    
    if (effectiveDate) effectiveDate.setHours(0, 0, 0, 0);
    if (endDate) endDate.setHours(0, 0, 0, 0);
    
    // Future: effective date hasn't been reached yet
    if (effectiveDate && effectiveDate > now) {
      return "future";
    }
    
    // Closed: past the plan end date
    if (endDate && endDate < now) {
      return "closed";
    }
    
    // Available: within the effective date range
    return "available";
  };

  // Fetch enrollments for this entity to compute total enrolled per plan
  const { data: clientEnrollments = [] } = useQuery<Enrollment[]>({
    queryKey: ["/api/enrollments", entityId],
    queryFn: async () => {
      const queryParam = clientId ? `clientId=${clientId}` : `prospectId=${prospectId}`;
      const res = await fetch(`/api/enrollments?${queryParam}`);
      if (!res.ok) throw new Error("Failed to fetch enrollments");
      return res.json();
    },
    enabled: !!entityId,
  });

  const refreshPlanLists = () => {
    // IMPORTANT: invalidate using the same queryKey shape as useClientPlans()
    // (it uses a numeric clientId).
    if (Number.isFinite(clientIdNum)) {
      queryClient.invalidateQueries({ queryKey: ["/api/client-plans", clientIdNum] });
      queryClient.refetchQueries({ queryKey: ["/api/client-plans", clientIdNum] });
    }
    queryClient.invalidateQueries({ queryKey: ["/api/plans"] });
  };

  // A client's plan years are renewed through the wizard (RenewPlanDialog).
  // This copy-renew is only for a plan with no client link behind it (a
  // prospect's template plan).
  const renewPlanMutation = useMutation({
    mutationFn: async (planId: number) => {
      const res = await fetch(`/api/plans/${planId}/renew`, {
        method: "POST",
        credentials: "include",
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.message || "Failed to renew plan");
      return { planId, newPlan: body };
    },
    onSuccess: ({ planId, newPlan }) => {
      if (Number.isFinite(planId) && Number.isFinite(Number(newPlan?.id))) {
        setLastRenewal({ fromPlanId: planId, toPlanId: Number(newPlan.id) });
      }
      queryClient.setQueryData(["/api/plans"], (oldData: any) => {
        if (!Array.isArray(oldData)) return oldData;
        const withoutDupes = oldData.filter((p: any) => p?.id !== newPlan?.id);
        return [newPlan, ...withoutDupes];
      });
      refreshPlanLists();
      toast({
        title: "Plan renewed successfully",
        description: "The plan has been duplicated with dates set 1 year forward. Please review the rates for accuracy.",
        variant: "success",
      });
    },
    onError: (error: any) => {
      setLastRenewal(null);
      toast({
        title: "Failed to renew plan",
        description: error.message || "An error occurred while renewing the plan",
        variant: "destructive",
      });
    },
  });

  // Re-run the enrollment carry-over of a renewal — picks up people who
  // enrolled after the plan was renewed.
  const carryOverMutation = useMutation({
    mutationFn: async (clientPlanId: number) => {
      const res = await apiRequest("POST", `/api/client-plans/${clientPlanId}/carry-over-enrollments`);
      return res.json();
    },
    onSuccess: (result: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/enrollments", entityId] });
      const parts = [
        result?.notApplicable ??
          `${result?.carried ?? 0} employee${result?.carried === 1 ? "" : "s"} pre-enrolled for the new plan year.`,
      ];
      if (result?.alreadyElected) parts.push(`${result.alreadyElected} already covered for it.`);
      if (result?.skipped?.length) {
        parts.push(`Not carried over: ${result.skipped.map((s: any) => `${s.name} (${s.reason})`).join(", ")}.`);
      }
      toast({ title: "Enrollments carried over", description: parts.join(" "), variant: "success" });
    },
    onError: (error: any) => {
      toast({
        title: "Couldn't carry over enrollments",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  const markRatesReviewedMutation = useMutation({
    mutationFn: async (planId: number) => {
      await apiRequest("PATCH", `/api/plans/${planId}`, {
        needsRateReview: false,
      });
    },
    onSuccess: async (_data, planId) => {
      queryClient.setQueryData(["/api/plans"], (oldData: any) => {
        if (!Array.isArray(oldData)) return oldData;
        return oldData.map((p: any) =>
          p?.id === planId ? { ...p, needsRateReview: false } : p
        );
      });

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["/api/plans"] }),
        queryClient.invalidateQueries({ queryKey: ["/api/agency-plans"] }),
        Number.isFinite(clientIdNum)
          ? queryClient.invalidateQueries({
              queryKey: ["/api/client-plans", clientIdNum],
            })
          : Promise.resolve(),
      ]);

      toast({
        title: "Marked reviewed",
        description: "The rate review label has been cleared.",
        variant: "success",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to mark reviewed",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  // Fetch plan contributions to check which plans are ready for enrollment
  const { data: planContributions = [] } = useQuery<any[]>({
    queryKey: ["/api/plan-contributions"],
    queryFn: async () => {
      const res = await fetch("/api/plan-contributions");
      if (!res.ok) return [];
      return res.json();
    },
  });

  // Helper to check if a plan has contributions configured
  const hasContributions = (planId: number) => {
    return (planContributions as any[]).some((c) => c.planId === planId);
  };

  // FSA readiness lives in fsaPlanConfig (not planContributions) — see getPlanEnrollmentStatus.
  const { fsaConfigByPlanId } = useFsaConfigs();

  // Total Enrolled counts pending (future-dated) elections too, split from the
  // active ones (DEV-317) — the same rule the Benefits Overview uses.
  const enrollmentsByPlanId = new Map<number, Enrollment[]>();
  (clientEnrollments as Enrollment[]).forEach((e) => {
    const list = enrollmentsByPlanId.get(e.planId) || [];
    list.push(e);
    enrollmentsByPlanId.set(e.planId, list);
  });

  // Sort plans: place any renewed plan (plan.renewedFromPlanId) directly above its source plan.
  // This is computed from persisted data, so it stays correct after page refresh.
  const sortedPlansData = React.useMemo(() => {
    if (!Array.isArray(plansData)) return [];

    const list = [...plansData];

    const renewalsBySourcePlanId = new Map<number, any[]>();
    const renewalPlanIds = new Set<number>();

    for (const p of list) {
      const sourceId = Number((p as any)?.renewedFromPlanId);
      if (!Number.isFinite(sourceId)) continue;
      renewalPlanIds.add(Number((p as any)?.id));
      const bucket = renewalsBySourcePlanId.get(sourceId) || [];
      bucket.push(p);
      renewalsBySourcePlanId.set(sourceId, bucket);
    }

    const ordered: any[] = [];

    for (const p of list) {
      const planId = Number((p as any)?.id);
      if (renewalPlanIds.has(planId)) continue;

      const renewals = renewalsBySourcePlanId.get(planId) || [];
      // If multiple renewals exist, show the most recent effective date first.
      renewals.sort((a, b) => {
        const ad = (parseDateLocal((a as any)?.effectiveDate ?? (a as any)?.createdAt) ?? new Date(0)).getTime();
        const bd = (parseDateLocal((b as any)?.effectiveDate ?? (b as any)?.createdAt) ?? new Date(0)).getTime();
        return bd - ad;
      });

      ordered.push(...renewals);
      ordered.push(p);
    }

    // Edge case: if a renewal exists but its source plan isn't present, append it.
    for (const p of list) {
      const planId = Number((p as any)?.id);
      if (!renewalPlanIds.has(planId)) continue;
      if (!ordered.some((x) => Number((x as any)?.id) === planId)) {
        ordered.push(p);
      }
    }

    // Final step: ensure the most recent renewal (just clicked) appears directly above
    // its source plan, even if persisted linkage isn't present yet.
    if (lastRenewal) {
      const fromIndex = ordered.findIndex(
        (p: any) => Number(p?.id) === lastRenewal.fromPlanId
      );
      const toIndex = ordered.findIndex(
        (p: any) => Number(p?.id) === lastRenewal.toPlanId
      );
      if (fromIndex >= 0 && toIndex >= 0 && toIndex !== fromIndex - 1) {
        const [moved] = ordered.splice(toIndex, 1);
        const insertAt = Math.max(0, toIndex < fromIndex ? fromIndex - 1 : fromIndex);
        ordered.splice(insertAt, 0, moved);
      }
    }

    // Plan years of the same plan (link renewal) sit together, renewal year
    // directly above the year it renews: they share the plan's first index.
    const firstIndexByPlanId = new Map<number, number>();
    ordered.forEach((p, i) => {
      const planId = Number((p as any)?.id);
      if (!firstIndexByPlanId.has(planId)) firstIndexByPlanId.set(planId, i);
    });
    const startOf = (p: any) => (parseDateLocal(p?.effectiveDate) ?? new Date(0)).getTime();

    // Order by the canonical plan-type rank (same as /benefits). Stable via the
    // index tiebreaker, so renewals stay directly above their source plan and
    // within-type order is preserved; unknown types sort last.
    return ordered
      .map((p, i) => [p, firstIndexByPlanId.get(Number((p as any)?.id)) ?? i, i] as const)
      .sort(
        (a, b) =>
          (planTypeRank(a[0]?.type) - planTypeRank(b[0]?.type)) ||
          (a[1] - b[1]) ||
          (startOf(b[0]) - startOf(a[0])) ||
          (a[2] - b[2]),
      )
      .map(([p]) => p);
  }, [plansData, lastRenewal]);

  // Plan years per plan: a row whose plan has a later plan year was already
  // renewed; a row with an earlier one is the renewal year.
  const planYearsByPlanId = React.useMemo(() => {
    const map = new Map<number, any[]>();
    (Array.isArray(plansData) ? plansData : []).forEach((p: any) => {
      const planId = Number(p?.id);
      map.set(planId, [...(map.get(planId) || []), p]);
    });
    return map;
  }, [plansData]);

  // Renewed = a later plan year of the same plan exists, or the renewal moved
  // the client onto another plan (renewedToClientPlanId, from the renewal
  // record).
  const isAlreadyRenewed = (plan: any) =>
    hasLaterPlanYear(plan, planYearsByPlanId.get(Number(plan?.id)) || []) ||
    plan?.renewedToClientPlanId != null;
  // Renew is only offered in the three months before a plan year renews (and
  // once it is overdue) — there is nothing to renew for the rest of the year.
  // Not for the agency's own plans: that link carries no plan year.
  // Deliberately uncapped on the "after" side: a human can always manually
  // renew a very-overdue plan, so this gates the Renew menu item too.
  const canRenewPlan = (plan: any) =>
    !isAgencyContext && !isAlreadyRenewed(plan) && isRenewalWindowOpen(plan);
  // Capped at 12 months past renewal so stale data doesn't nag forever;
  // canRenewPlan above stays uncapped on purpose (see its comment).
  const plansDueForRenewal = (Array.isArray(plansData) ? plansData : []).filter(
    (plan: any) =>
      !isAgencyContext && !isAlreadyRenewed(plan) && isRenewalWindowOpen(plan, new Date(), 3, 12),
  ).length;

  // Per-state counts so the filter shows where plans live — a freshly added
  // future-effective plan is otherwise invisible under the "Available" filter
  // and looks like the add silently failed.
  const planStateCounts = React.useMemo(() => {
    const counts = { all: sortedPlansData.length, available: 0, closed: 0, future: 0 };
    sortedPlansData.forEach((plan: any) => {
      counts[getPlanState(plan)]++;
    });
    return counts;
  }, [sortedPlansData]);

  // Default filter, decided ONCE from the loaded counts (so it doesn't flip
  // under the user as plans are added/removed, and doesn't flash "Available"
  // before the fetch returns). "Available" when it has plans; otherwise the
  // state that has them ("Future" alone, or "All").
  const [settledDefaultFilter, setSettledDefaultFilter] = React.useState<PlanStateFilter | null>(null);
  const defaultFilter: PlanStateFilter | null =
    settledDefaultFilter ??
    (plansLoading ? null : defaultPlanStateFilter(planStateCounts, { arrivedWithNewPlans }));
  React.useEffect(() => {
    if (settledDefaultFilter == null && defaultFilter != null) setSettledDefaultFilter(defaultFilter);
  }, [settledDefaultFilter, defaultFilter]);
  const planStateFilter: PlanStateFilter = chosenPlanStateFilter ?? defaultFilter ?? "available";

  // Filter plans by state
  const filteredPlansData = React.useMemo(() => {
    if (planStateFilter === "all") return sortedPlansData;
    return sortedPlansData.filter((plan: any) => getPlanState(plan) === planStateFilter);
  }, [sortedPlansData, planStateFilter]);
  const hiddenPlanCount = plansLoading ? 0 : planStateCounts.all - filteredPlansData.length;
  const emptyState = planListEmptyState({
    counts: planStateCounts,
    filter: planStateFilter,
    visibleCount: filteredPlansData.length,
    loading: plansLoading,
  });

  // Converting an RFP proposal lands here with new plans mixed into an existing
  // list. The converter redirects with ?newPlans=<ids> and those rows are
  // highlighted. Read from window.location to avoid threading a prop through
  // ClientDetail -> BenefitsTab -> here.
  const newPlanIds = React.useMemo(() => {
    const raw = new URLSearchParams(window.location.search).get("newPlans");
    return new Set(
      (raw ?? "")
        .split(",")
        .map((n) => parseInt(n, 10))
        .filter(Number.isFinite),
    );
  }, []);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Available Plans
          </CardTitle>
          {onAddPlanClick ? (
            <PermissionButton
              permission={editPermission}
              whenDenied="hide"
              onClick={onAddPlanClick}
            >
              <Shield className="h-4 w-4 mr-2" />
              Add Plan
            </PermissionButton>
          ) : primaryActionLabel && onPrimaryAction ? (
            <PermissionButton
              permission="clients:edit"
              whenDenied="hide"
              onClick={onPrimaryAction}
            >
              <Shield className="h-4 w-4 mr-2" />
              {primaryActionLabel}
            </PermissionButton>
          ) : null}
        </div>
      </CardHeader>
      <CardContent>
        <AlertDialog open={isReviewDialogOpen} onOpenChange={setIsReviewDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Mark rates reviewed?</AlertDialogTitle>
              <AlertDialogDescription>
                {reviewPlan
                  ? `This will remove the rate review label from "${reviewPlan.name}".`
                  : "This will remove the rate review label."}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                onClick={() => {
                  setIsReviewDialogOpen(false);
                  setReviewPlan(null);
                }}
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (!reviewPlan) return;
                  markRatesReviewedMutation.mutate(reviewPlan.id);
                  setIsReviewDialogOpen(false);
                  setReviewPlan(null);
                }}
                disabled={markRatesReviewedMutation.isPending || !reviewPlan}
              >
                Mark reviewed
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <div className="flex items-center gap-4 mb-4">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Plan State:</span>
            <Select value={planStateFilter} onValueChange={(value: any) => setPlanStateFilter(value)}>
              <SelectTrigger className="w-[140px] h-8" data-testid="plans-state-filter">
                <SelectValue placeholder="Plan state" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All ({planStateCounts.all})</SelectItem>
                <SelectItem value="available">Available ({planStateCounts.available})</SelectItem>
                <SelectItem value="closed">Closed ({planStateCounts.closed})</SelectItem>
                <SelectItem value="future">Future ({planStateCounts.future})</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {hiddenPlanCount > 0 && (
            <button
              type="button"
              className="text-sm text-muted-foreground hover:text-foreground hover:underline"
              data-testid="plans-filter-hidden-hint"
              onClick={() => setPlanStateFilter("all")}
            >
              {hiddenPlanCount} more plan{hiddenPlanCount === 1 ? "" : "s"} hidden by
              this filter — show all
            </button>
          )}
        </div>

        {canEditPlans && plansDueForRenewal > 0 && (
          <div className="mb-4 flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
            <RefreshCw className="h-4 w-4 shrink-0" />
            <span>
              {plansDueForRenewal} plan{plansDueForRenewal === 1 ? " is" : "s are"} coming up for
              renewal. Use Renew in the plan's menu to set up the next plan year.
            </span>
          </div>
        )}

        <div className="rounded-md border">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    Plan Name
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    Carrier
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    Coverage Type
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    Policy Number
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    Effective Date
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    Total Enrolled
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    Plan State
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredPlansData?.length ? (
                  filteredPlansData.map((plan: any) => {
                    const effectivePlan = getEffectivePlan(plan);
                    const enrolledLabel = formatEnrolledLabel(
                      planEnrollmentCounts(enrollmentsByPlanId.get(plan.id)),
                    );
                    // Shared predicate drives both the Status and Plan State
                    // badges, so they never contradict (e.g. "Ready/Available"
                    // for a plan that's actually hidden from employees).
                    const enrollmentStatus = getPlanEnrollmentStatus(
                      effectivePlan.type,
                      {
                        fsaConfig: fsaConfigByPlanId.get(effectivePlan.id),
                        hasContributions: hasContributions(plan.id),
                      },
                    );
                    const goToFsaConfig = () => {
                      if (!setLocation) return;
                      const queryParams =
                        agencyId && !clientId
                          ? `agencyId=${agencyId}`
                          : clientId
                          ? `clientId=${clientId}`
                          : "";
                      setLocation(
                        `/plans/${plan.id}?${queryParams ? `${queryParams}&` : ""}tab=fsa-config`,
                      );
                    };
                    const isNewlyCreated = newPlanIds.has(Number(plan.id));
                    const samePlanYears = planYearsByPlanId.get(Number(plan.id)) || [];
                    const canRenew = canEditPlans && canRenewPlan(plan);
                    // Only when the renewal moved the client to another plan —
                    // renewed in place, enrollments already continue.
                    const renewedInto =
                      plan.renewedToClientPlanId != null
                        ? (plansData as any[]).find((p) => p.clientPlanId === plan.renewedToClientPlanId)
                        : null;
                    const canCarryOver =
                      canEditPlans && !!clientId && renewedInto != null && renewedInto.id !== plan.id;
                    const canRemove = canEditPlans && showDelete && !!deletePlanMutation;
                    // Upcoming plan year of a plan that is already in place.
                    const isRenewalYear =
                      getPlanState(plan) === "future" &&
                      samePlanYears.some(
                        (other: any) => other !== plan && hasLaterPlanYear(other, [plan]),
                      );
                    return (
                      <tr
                        key={plan.clientPlanId ?? plan.id}
                        className={`border-b hover:bg-muted/25 ${
                          isNewlyCreated
                            ? "bg-emerald-50 dark:bg-emerald-950/30 ring-1 ring-inset ring-emerald-400/50"
                            : ""
                        }`}
                      >
                        <td className="px-4 py-3">
                          <div>
                            <div className="flex items-center gap-2">
                              {isNewlyCreated && (
                                <Badge className="bg-emerald-600 text-white text-[10px] shrink-0">New</Badge>
                              )}
                              <p className="font-medium text-sm">
                                {effectivePlan.name}
                              </p>
                              {isRenewalYear && (
                                <Badge variant="outline" className="text-[10px] shrink-0 bg-blue-50 text-blue-700 border-blue-200">
                                  Renewal year
                                </Badge>
                              )}
                              {(effectivePlan as any).needsRateReview && (
                                <button
                                  type="button"
                                  className="text-left"
                                  onClick={() => {
                                    setReviewPlan({
                                      id: Number(plan.id),
                                      name: String(effectivePlan.name || "Plan"),
                                    });
                                    setIsReviewDialogOpen(true);
                                  }}
                                >
                                  <Badge
                                    variant="secondary"
                                    className="text-xs bg-amber-100 text-amber-800 border-amber-300 flex items-center gap-1 cursor-pointer select-none"
                                  >
                                    <AlertCircle className="h-3 w-3" />
                                    Review plan rates to ensure accuracy
                                  </Badge>
                                </button>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {effectivePlan.description || "No description"}
                            </p>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {(effectivePlan as any).carrier ??
                            `Carrier #${effectivePlan.carrierId}`}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <Badge variant="outline" className="capitalize">
                            {effectivePlan.type}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-sm font-mono">
                          {/* Client-specific carrier group number (client_plans
                              link) — the shared library plan's planCode is a
                              system id, not this client's policy number. */}
                          {(effectivePlan as any).groupNumber || (
                            <span
                              className="font-sans text-muted-foreground"
                              title="No carrier group number recorded for this client yet — set it from the plan's Overview tab."
                            >
                              Not set
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {formatDateSafe(effectivePlan.effectiveDate) !== "TBD"
                            ? formatDateSafe(effectivePlan.effectiveDate)
                            : formatDateSafe(plan.createdAt)}
                        </td>
                        <td className="px-4 py-3 text-sm" data-testid="plan-enrolled-count">
                          {enrolledLabel}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {enrollmentStatus.configReady ? (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                              Ready
                            </Badge>
                          ) : (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge
                                    variant="outline"
                                    onClick={enrollmentStatus.hiddenFromEmployees ? goToFsaConfig : undefined}
                                    className={`bg-amber-50 text-amber-700 border-amber-200 ${
                                      enrollmentStatus.hiddenFromEmployees ? "cursor-pointer" : "cursor-help"
                                    }`}
                                  >
                                    <AlertTriangle className="w-3 h-3 mr-1" />
                                    Setup Required
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>
                                    {enrollmentStatus.reason ??
                                      "Contributions must be configured before employees can enroll"}
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {(() => {
                            const state = getPlanState(plan);
                            if (state === "available") {
                              // In its date window, but a config gap keeps it
                              // hidden from employees → don't claim "Available".
                              if (enrollmentStatus.hiddenFromEmployees) {
                                return (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Badge
                                          variant="outline"
                                          onClick={goToFsaConfig}
                                          className="bg-amber-50 text-amber-700 border-amber-200 cursor-pointer"
                                        >
                                          <AlertTriangle className="w-3 h-3 mr-1" />
                                          Hidden
                                        </Badge>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>
                                          In effect, but hidden from employees until FSA limits are set.
                                        </p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                );
                              }
                              return (
                                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                  Available
                                </Badge>
                              );
                            } else if (state === "closed") {
                              return (
                                <Badge variant="outline" className="bg-gray-50 text-gray-600 border-gray-300">
                                  Closed
                                </Badge>
                              );
                            } else {
                              return (
                                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                  Future
                                </Badge>
                              );
                            }
                          })()}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            {showViewDetails && setLocation && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  // Build query params based on context
                                  // For agency-level plans, use agencyId; for client/prospect plans, use clientId
                                  const queryParams = agencyId && !clientId
                                    ? `agencyId=${agencyId}`
                                    : clientId
                                    ? `clientId=${clientId}`
                                    : "";
                                  // clientPlanId opens THIS row's plan year
                                  // (a renewed plan has more than one).
                                  const planYearParam =
                                    clientId && plan.clientPlanId
                                      ? `&clientPlanId=${plan.clientPlanId}`
                                      : "";
                                  setLocation(
                                    `/plans/${plan.id}${queryParams ? `?${queryParams}${planYearParam}` : ""}`
                                  );
                                }}
                              >
                                View Details
                              </Button>
                            )}
                            {(canRenew || canCarryOver || canRemove) && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    aria-label={`More actions for ${effectivePlan.name}`}
                                  >
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {canRenew && (
                                    <DropdownMenuItem
                                      onSelect={() =>
                                        plan.clientPlanId
                                          ? setRenewDialogPlan(plan)
                                          : setConfirmAction({ kind: "copyRenew", plan })
                                      }
                                    >
                                      <RefreshCw className="mr-2 h-4 w-4" />
                                      Renew
                                    </DropdownMenuItem>
                                  )}
                                  {canCarryOver && (
                                    <DropdownMenuItem
                                      disabled={carryOverMutation.isPending}
                                      onSelect={() => carryOverMutation.mutate(plan.clientPlanId)}
                                    >
                                      <Users className="mr-2 h-4 w-4" />
                                      Carry over enrollments
                                    </DropdownMenuItem>
                                  )}
                                  {canRemove && (canRenew || canCarryOver) && <DropdownMenuSeparator />}
                                  {canRemove && (
                                    <DropdownMenuItem
                                      className="text-destructive focus:text-destructive"
                                      onSelect={() => setConfirmAction({ kind: "remove", plan })}
                                    >
                                      <Trash2 className="mr-2 h-4 w-4" />
                                      Remove
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : emptyState.kind === "loading" ? (
                  <tr>
                    <td
                      colSpan={9}
                      className="px-4 py-8 text-center text-muted-foreground"
                      data-testid="plans-loading"
                    >
                      <p>Loading plans…</p>
                    </td>
                  </tr>
                ) : emptyState.kind === "filtered-out" ? (
                  // This state is empty but the client HAS plans elsewhere —
                  // never "Add First Plan" here (DEV-319).
                  <tr>
                    <td
                      colSpan={9}
                      className="px-4 py-8 text-center text-muted-foreground"
                    >
                      <Shield className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p data-testid="plans-filter-empty-hint">{emptyState.message}</p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        data-testid="plans-filter-empty-show-all"
                        onClick={() => setPlanStateFilter("all")}
                      >
                        Show all
                      </Button>
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td
                      colSpan={9}
                      className="px-4 py-8 text-center text-muted-foreground"
                      data-testid="plans-empty"
                    >
                      <Shield className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No plans available</p>
                      {onAddPlanClick ? (
                        <PermissionButton
                          permission={editPermission}
                          whenDenied="hide"
                          className="mt-2"
                          size="sm"
                          data-testid="plans-add-first"
                          onClick={onAddPlanClick}
                        >
                          Add First Plan
                        </PermissionButton>
                      ) : primaryActionLabel && onPrimaryAction ? (
                        <PermissionButton
                          permission="clients:edit"
                          whenDenied="hide"
                          className="mt-2"
                          size="sm"
                          onClick={onPrimaryAction}
                        >
                          {primaryActionLabel}
                        </PermissionButton>
                      ) : null}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {clientId && (
          <RenewPlanDialog
            open={renewDialogPlan != null}
            onOpenChange={(open) => !open && setRenewDialogPlan(null)}
            plan={renewDialogPlan}
            clientId={clientId}
            onRenewed={() => {
              refreshPlanLists();
              queryClient.invalidateQueries({ queryKey: ["/api/enrollments", entityId] });
              // Carried-over contributions decide Ready vs Setup Required.
              queryClient.invalidateQueries({ queryKey: ["/api/plan-contributions"] });
              // The renewal year starts in the future, which the default
              // "Available" filter hides — show it next to the current year.
              setPlanStateFilter("all");
            }}
          />
        )}

        <AlertDialog open={confirmAction != null} onOpenChange={(open) => !open && setConfirmAction(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {confirmAction?.kind === "copyRenew" ? "Renew Plan" : "Remove Plan"}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {confirmAction?.kind === "copyRenew"
                  ? `This will create a copy of "${confirmAction.plan?.name}" with the effective date set to 1 year from the current effective date. You will need to review and update the plan rates to ensure accuracy.`
                  : deleteMode === "removeFromAgency"
                    ? `Are you sure you want to remove "${confirmAction?.plan?.name}" from your agency's plans? The plan itself stays in the library.`
                    : `Are you sure you want to remove "${confirmAction?.plan?.name}" from this client? This action cannot be undone.`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className={
                  confirmAction?.kind === "remove"
                    ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    : undefined
                }
                onClick={() => {
                  if (!confirmAction) return;
                  const { kind, plan } = confirmAction;
                  if (kind === "copyRenew") renewPlanMutation.mutate(Number(plan.id));
                  else {
                    deletePlanMutation.mutate(
                      deleteMode === "removeFromAgency" ? plan.agencyPlanId : plan.clientPlanId
                    );
                  }
                  setConfirmAction(null);
                }}
              >
                {confirmAction?.kind === "copyRenew" ? "Renew Plan" : "Remove Plan"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
};

export default AvailablePlans;
